import { KeyboardEvent, memo, useCallback, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Collapse, Table, TableBody, TableCell, TableRow, TextField } from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { DATA_GRID_FIRST_PAGE } from 'common/constants/pagination';
import { useUpdatePalmDataGridPage } from 'common/hooks/useUpdatePalmDataGridPage';
import { isFilterApplied } from 'common/utils/filterHelpers';

import { selectPaginationModel } from '../PalmDataGrid/palmDataGridSlice';

import { FilterBarProps } from './FilterBar.types';
import { FilterDropdown } from './FilterDropdown';
import { SearchTextBubble } from './SearchTextBubble';

const FilterBarComponent = ({
    shouldShowFilter,
    setShouldShowFilter,
    filterBarDisabled,
    searchFilterDisabled,
    filter,
    paginationMode
}: FilterBarProps) => {
    const [filterFieldValue, setFilterFieldValue] = useState<string>('');
    const paginationModel = useAppSelector(selectPaginationModel);
    const { updatePalmDataGridPage } = useUpdatePalmDataGridPage();
    const sxTableRow = filterBarDisabled ? { '& td': { border: 0 },  pointerEvents: 'none' } : { '& td': { border: 0 } };

    const onEnterPress = useCallback((e: KeyboardEvent): void => {
        const { value } = e.target as HTMLInputElement;
        if (e.key === 'Enter' && value) {
            const lowerCaseValue: string = value.toLowerCase().trim();
            filter?.addFilter(lowerCaseValue);
            setFilterFieldValue('');
            updatePalmDataGridPage(paginationMode, DATA_GRID_FIRST_PAGE, paginationModel);
        }
    }, [paginationModel, filter, paginationMode, updatePalmDataGridPage]);

    const onSearchBubbleDelete = useCallback((name: string): void => {
        filter?.removeFilter(name);
        updatePalmDataGridPage(paginationMode, DATA_GRID_FIRST_PAGE, paginationModel);
    }, [paginationModel, filter, paginationMode, updatePalmDataGridPage]);

    const handleFilterFieldValue: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        e => setFilterFieldValue(e.target.value),
        []
    );
    const handleShowFilter = useCallback(() => {
        if (isFilterApplied(filter?.filters)) {
            filter?.clearFilters();
            updatePalmDataGridPage(paginationMode, DATA_GRID_FIRST_PAGE, paginationModel);
        }
        setShouldShowFilter(!shouldShowFilter);
    }, [paginationModel, filter, paginationMode, setShouldShowFilter, shouldShowFilter, updatePalmDataGridPage]);

    return (
        <Collapse in={shouldShowFilter} timeout="auto" unmountOnExit sx={{ '& .MuiFormControl-root': { marginRight: '10px' } }}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    padding: '8px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    borderRadius: '4px',
                    border: 1,
                    borderColor: 'primary.light',
                    color: 'secondary.dark',
                    backgroundColor: 'primary.ultraLight',
                }}
            >
                <Table data-testid='filterBar' size='small'>
                    <TableBody>
                        <TableRow sx={sxTableRow}>
                            <TableCell align='left'>
                                <TextField
                                    sx={{
                                        backgroundColor: 'background.paper',
                                        borderRadius: '4px',
                                        width: '20%'
                                    }}
                                    id='outlined-basic'
                                    size='small'
                                    placeholder='Filter By Keyword'
                                    variant='outlined'
                                    onChange={handleFilterFieldValue}
                                    onKeyDown={onEnterPress}
                                    value={filterFieldValue}
                                    disabled={searchFilterDisabled}
                                />
                                {
                                    filter?.filters.keywords.map((bubble: string) => <SearchTextBubble text={bubble}
                                        key={bubble}
                                        onDelete={onSearchBubbleDelete}/>)
                                }
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle', marginTop: '6px', textAlign: 'end', width: '23%' }}>
                                {
                                    filter?.dropdownProps?.map(
                                        ({ dropdownInfo, dropdownReducer }) =>

                                            <FilterDropdown
                                                key={dropdownInfo.id}
                                                dropdown={dropdownInfo}
                                                onCheckboxChange={dropdownReducer}
                                                checkedKeys={filter?.filters?.dropdowns?.[dropdownInfo.id].keys}
                                                paginationMode={paginationMode}
                                            />
                                    )
                                }
                            </TableCell>
                            <TableCell align='right' sx={{ paddingRight: 0, verticalAlign: 'top' }} style={{ width: '5%' }} title='Clear Filters & Close'>
                                <Button
                                    sx={{ alignItems: 'right', marginTop: '2px' }}
                                    onClick={handleShowFilter}
                                >
                                    <CloseIcon sx={{ color: 'secondary.main' }} data-testid='closeButton' />
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Box>
        </Collapse >
    );
};

export const FilterBar = memo(FilterBarComponent);
